// utils/useVideoLoader.ts
import { ref } from 'vue'
import AxiosVideoService from '../axiosVideoService'

const axiosVideoService = new AxiosVideoService()

// 定義字幕欄位的型別
interface TranscriptItem {
  id: string | number
  text: string
  start: number
  end: number
  ori_zh_text: string
  zh_text: string[]
  en_text: string
  site_text: string
  state: boolean
  translated_languages?: Array<{ language: string; text: string }>
}


// 影片載入相關的 function
export function useVideoLoader() {


  // 音訊語言、字幕資料、來源平台、影片ID與影片完整資料
  const videoTitle = ref<string>('')
  const videoLang = ref<string>()
  const videoTranscript = ref<any[]>([])
  const videoType = ref<'Youtube' | 'Vimeo' | 'AWS'>('Youtube')
  const originId = ref<string>('')  // ✅ Ref<string>

  // 從 Vimeo 影片網址中提取影片 ID
  function extractVimeoId(url: string) {
    const match = url.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/)
    return match ? match[1] : null
  }

function processTranscript(raw: any[], videoLang: string | undefined) {
  const lang = (videoLang || '').toLowerCase();

  const normStr = (v: any) => {
    if (v == null) return '';
    if (Array.isArray(v)) return v.filter(Boolean).join(' ');
    return String(v);
  };

  const pickTL = (item: any, target: string) => {
    const list = Array.isArray(item?.translated_languages) ? item.translated_languages : [];
    const hit = list.find((t: any) => String(t?.language || '').toLowerCase() === target.toLowerCase());
    return normStr(hit?.text);
  };

  // 依原文語言決定「原文」與「要顯示的翻譯」
  // - Chinese(簡體/繁體)：原文=中文；翻譯=英文
  // - Spanish：原文=西文；翻譯=英文
  // - English：原文=英文；翻譯=西文
  const isChinese = lang.includes('chinese');
  const isSpanish = lang.includes('spanish');
  const isEnglish = lang.includes('english');

  return raw.map((item) => {
    const text = normStr(item.text);
    const zhTextStr = normStr(item.zh_text);
    const enTextStr = normStr(item.en_text);

    let source = '';      // 原文（顯示在你 template 的「en_text」那一行）
    let translate = '';   // 翻譯（顯示在你 template 的「zh_text」那一行；最後會拆成陣列）

    if (isChinese) {
      // 原文：中文
      source = text || zhTextStr;
      // 翻譯：英文（優先 TL 英文 → en_text → zh_text 補救）
      translate = pickTL(item, 'English') || enTextStr || zhTextStr;
    } else if (isSpanish) {
      // 原文：西文（常見在 en_text 或 text）
      source = text || enTextStr;
      // 翻譯：英文（優先 TL 英文 → zh_text → text 補救）
      translate = pickTL(item, 'English') || zhTextStr || text;
    } else if (isEnglish) {
      // 原文：英文（常見在 en_text 或 text）
      source = text || enTextStr;
      // 翻譯：西文（優先 TL 西文 → zh_text(有時候被塞英文/中文) 作為最後備援）
      translate = pickTL(item, 'English') || zhTextStr || '';
    } else {
      // 找不到語言就盡量兜
      source = text || enTextStr || zhTextStr;
      translate = pickTL(item, 'English') || pickTL(item, 'Spanish') || zhTextStr || '';
    }

    const zhArr = translate.trim()
      ? translate.trim().split(/\s+/).filter(Boolean)
      : [];

    return {
      ...item,
      zh_text: source ? source.trim().split(/\s+/).filter(Boolean) : [], // 原文（陣列）
      en_text: translate || '',                                         // 翻譯（字串）

      // 保留原欄位避免資訊流失
      text: item.text ?? null,
      translated_languages: item.translated_languages ?? null
    };
  });
}

async function getVideo(videoId: string) {
  try {
    const res = await axiosVideoService.getVideoById(videoId);
    const data = res.data;

    videoTitle.value = data.title || data.name || 'video';
    videoLang.value = data.language;  // <- 決定原文語言的關鍵

    if (Array.isArray(data.transcript)) {
      videoTranscript.value = processTranscript(data.transcript, data.language);
    } else {
      videoTranscript.value = [];
    }

    let video_url = data.video_url;

    if (video_url.includes('https://youtube.com/watch?v=')) {
      video_url = video_url.replace('https://youtube.com/watch?v=', '');
    } else if (video_url.includes('https://www.youtube.com/watch?v=')) {
      video_url = video_url.replace('https://www.youtube.com/watch?v=', '');
    } else if (video_url.includes('https://youtu.be/')) {
      video_url = video_url.replace('https://youtu.be/', '');
    } else if (video_url.includes('https://player.vimeo.com/video/')) {
      videoType.value = 'Vimeo';
    } else if (video_url.includes('https://dl00aoemc1w8g.cloudfront.net/videos/')) {
      videoType.value = 'AWS';
    }

    if (videoType.value === 'Youtube') {
      originId.value = video_url.substring(0, 11);
    } else if (videoType.value === 'Vimeo') {
      originId.value = extractVimeoId(video_url) || '';
    } else if (videoType.value === 'AWS') {
      originId.value = video_url;
    }
  } catch (err: any) {
    console.error('video failed', err);
  } finally {
    // no-op
  }
}


  function sanitizeFileName(name: string) {
    // 去除非法字元與多餘空白，避免太長
    return (name || 'video')
      .replace(/[\\\/:*?"<>|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 100)
  }


    function download_transcripts() {
      // 安全防呆
      const list = Array.isArray(videoTranscript.value) ? videoTranscript.value : []

      // 中文一行 + 英文一行 + 空行
      const content = list.map((item: any) => {
        const zh = item?.ori_zh_text ?? item?.text ?? ''
        const en = item?.en_text ?? item?.site_text ?? ''
        return `${zh}\n${en}\n`
      }).join('\n')

      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url

      const safeTitle = sanitizeFileName(videoTitle.value)
      a.download = `${safeTitle}.txt`     

      document.body.appendChild(a)
      a.click()
      a.remove()

      URL.revokeObjectURL(url)
    }

  // 將外部需要使用的 ref 與方法匯出
  return {
    videoTitle,
    videoLang,
    videoTranscript,
    videoType,
    originId,
    getVideo,
    download_transcripts
  }
}